import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Sparkles, TrendingUp, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Public /benchmarks — anonymized industry leaderboard.
 * Sells the gap: prospects see how their industry compares and can run the
 * 60-second quiz to find where they sit inside it.
 */
export default function Benchmarks() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total_industries: 0, live_industries: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/benchmarks`).then(({ data }) => {
      setRows(data.benchmarks || []);
      setMeta({ total_industries: data.total_industries || 0, live_industries: data.live_industries || 0 });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = q.trim()
    ? rows.filter((r) => r.industry.toLowerCase().includes(q.trim().toLowerCase()))
    : rows;

  const bestPct = rows.length ? rows[0].avg_score_pct : 0;
  const worstPct = rows.length ? rows[rows.length - 1].avg_score_pct : 0;
  const median = rows.length ? rows[Math.floor(rows.length / 2)].avg_score_pct : 0;

  return (
    <div style={{ minHeight: "100vh", background: "#FFF6EE", color: "#0A192F", fontFamily: "Inter,-apple-system,sans-serif" }} data-testid="benchmarks-page">
      {/* Top bar */}
      <div style={{ borderBottom: "1px solid #E5D9CB", background: "rgba(255,255,255,.7)", backdropFilter: "blur(8px)" }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/" style={{ fontFamily: "'Fraunces',Georgia,serif", fontWeight: 900, fontSize: 20, letterSpacing: "-0.02em", color: "#0A192F", textDecoration: "none" }}>
            Clients<span style={{ color: "#E24E1B" }}>OnRamp</span>
          </Link>
          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <Link to="/ai-visibility-quiz" style={{ fontSize: 13, color: "#3D4756", textDecoration: "none" }}>Run my score →</Link>
            <Link to="/" aria-label="Close and return home" data-testid="benchmarks-close" style={{ width: 34, height: 34, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#fff", border: "1px solid #E5D9CB", borderRadius: 50, color: "#5A6478", textDecoration: "none" }} title="Close">
              <X style={{ width: 16, height: 16 }} />
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "60px 24px 30px" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid #E5D9CB", padding: "6px 12px", fontFamily: "'JetBrains Mono',monospace", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 20 }}>
          <TrendingUp style={{ width: 12, height: 12, color: "#E24E1B" }} /> Live Industry Benchmarks
        </div>
        <h1 style={{ fontFamily: "'Fraunces',Georgia,serif", fontSize: 56, fontWeight: 900, lineHeight: 1.02, letterSpacing: "-0.03em", marginBottom: 16, maxWidth: 820 }}>
          How visible is your industry, <em style={{ fontStyle: "italic", color: "#E24E1B", fontWeight: 400 }}>really</em>?
        </h1>
        <p style={{ fontSize: 18, color: "#3D4756", maxWidth: 640, lineHeight: 1.55, marginBottom: 32 }}>
          Averaged AI Visibility scores across every audit we've run — grouped by industry. See where your competitors sit before you find out where you do.
        </p>

        {/* Stat row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16, marginBottom: 40 }}>
          {[
            { label: "Top industry avg", val: `${bestPct}%`, tone: "#059669" },
            { label: "Bottom industry avg", val: `${worstPct}%`, tone: "#DC2626" },
            { label: "Median across all", val: `${median}%`, tone: "#0A192F" },
            { label: "Industries tracked", val: meta.total_industries, tone: "#0A192F" },
          ].map((s) => (
            <div key={s.label} style={{ background: "#fff", border: "1px solid #E5D9CB", padding: "18px 20px" }}>
              <div style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "#5A6478", fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{s.label}</div>
              <div style={{ fontSize: 30, fontFamily: "'Fraunces',Georgia,serif", fontWeight: 900, color: s.tone, letterSpacing: "-0.02em" }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <input
          data-testid="benchmarks-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter industries — e.g. plumbing, dental, saas…"
          style={{ width: "100%", maxWidth: 520, padding: "14px 18px", fontSize: 15, background: "#fff", border: "1px solid #E5D9CB", outline: "none", color: "#0A192F", marginBottom: 24 }}
        />
      </div>

      {/* Leaderboard */}
      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 24px 80px" }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: "center", color: "#5A6478", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <Loader2 style={{ width: 18, height: 18, animation: "spin 1s linear infinite" }} /> Loading benchmarks…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 60, textAlign: "center", color: "#5A6478", background: "#fff", border: "1px solid #E5D9CB" }} data-testid="benchmarks-empty">
            No industries match "{q}". Try a broader term.
          </div>
        ) : (
          <div data-testid="benchmarks-list" style={{ background: "#fff", border: "1px solid #E5D9CB" }}>
            {filtered.map((row, i) => {
              const pct = row.avg_score_pct;
              const tone = pct >= 55 ? "#059669" : pct >= 40 ? "#D97706" : "#DC2626";
              const grade = pct >= 55 ? "A · dominant" : pct >= 40 ? "B · patchy" : pct >= 25 ? "C · thin" : "F · invisible";
              return (
                <div key={row.industry + i} data-testid={`benchmark-row-${row.industry.toLowerCase().replace(/[^a-z]+/g, "-")}`} style={{ display: "grid", gridTemplateColumns: "60px 1.6fr 3fr 1fr auto", gap: 20, alignItems: "center", padding: "18px 24px", borderBottom: i === filtered.length - 1 ? "none" : "1px solid #F0E6D8" }}>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 13, color: "#94A3B8", fontWeight: 600 }}>#{i + 1}</div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#0A192F", marginBottom: 2 }}>{row.industry}</div>
                    <div style={{ fontSize: 11, color: "#94A3B8", fontFamily: "'JetBrains Mono',monospace", letterSpacing: "0.05em" }}>
                      n={row.sample_size}{row.source === "live" ? " · live" : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: 1, height: 10, background: "#F1F5F9", borderRadius: 5, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${Math.max(3, pct)}%`, background: tone, borderRadius: 5, transition: "width 800ms ease-out" }} />
                    </div>
                    <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 15, fontWeight: 700, color: tone, minWidth: 52, textAlign: "right" }}>{pct}%</div>
                  </div>
                  <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: tone, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>{grade}</div>
                  <Link to={`/ai-visibility-quiz`} data-testid={`benchmark-cta-${i}`} style={{ fontSize: 12, color: "#E24E1B", textDecoration: "none", fontWeight: 600, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    Test yours <ArrowRight style={{ width: 12, height: 12 }} />
                  </Link>
                </div>
              );
            })}
          </div>
        )}

        {/* Final CTA */}
        <div style={{ marginTop: 40, padding: "40px 32px", background: "#0A192F", color: "#fff", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: "#38bdf8", fontFamily: "'JetBrains Mono',monospace", marginBottom: 14 }}>
            <Sparkles style={{ width: 12, height: 12 }} /> Curious where you sit?
          </div>
          <h2 style={{ fontFamily: "'Fraunces',Georgia,serif", fontSize: 34, fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 12, lineHeight: 1.15 }}>
            Get your <em style={{ fontStyle: "italic", color: "#38bdf8", fontWeight: 400 }}>own</em> AI Visibility score in 60 seconds.
          </h2>
          <p style={{ color: "#94A3B8", fontSize: 15, maxWidth: 480, margin: "0 auto 24px", lineHeight: 1.55 }}>
            Free · No credit card · See exactly where you rank inside your industry line.
          </p>
          <Link to="/ai-visibility-quiz" data-testid="benchmarks-cta-quiz" style={{ background: "linear-gradient(135deg,#0ea5e9,#2563eb)", color: "#fff", padding: "16px 30px", fontSize: 15, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, borderRadius: 8 }}>
            Run my 60-second audit <ArrowRight style={{ width: 16, height: 16 }} />
          </Link>
        </div>

        <p style={{ marginTop: 24, fontSize: 11, color: "#94A3B8", textAlign: "center", fontFamily: "'JetBrains Mono',monospace", letterSpacing: "0.05em" }}>
          Data anonymized · Live industries populate as we hit ≥ 3 audits per category · Baseline sampled from Q4 2025 audit runs
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
